import { useCallback } from 'react';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import { localStorageAvailable } from '@utils/localStorage';
import Cookies from '@utils/cookies';
import uaAnalytics from '@utils/uaAnalytics';
import Clock from '@utils/Clock';
import { sendWatched, sendWatchedPrevious } from '@api/fetchers/class/updaters';
import useConstants from '@api/hooks/useConstants';
import { getAnalyticsEventInterval } from '@api/selectors/constants';
import usePlayerStore from './usePlayerStore';
import Player from '../extras/Player';
import UnLive from '../extras/UnLive';

const player = new Player();
const clock = new Clock();
const viewer = new UnLive();

const usePlayerEvents = () => {
  const router = useRouter();
  const { query } = router;

  const me = useMyInfo(getMe);

  const { currentClassData } = usePlayerStore();

  const watchEventInterval = useConstants(getAnalyticsEventInterval);

  const isExternal = query?.external === '1';

  const handleOnSendWatched = () => sendWatched(currentClassData.uid);

  const handleOnSendWatchedPrevious = async () => {
    sendWatchedPrevious(currentClassData.uid).then((response) => {
      if (!response.error && localStorageAvailable()) {
        localStorage.setItem(
          `liveSessionsWatchEventSent[${currentClassData.uid}]`,
          true
        );
      }
    });
  };

  const setWatchTime = useCallback(() => {
    player.source = currentClassData.source;
    player.totalWatchTime = localStorageAvailable()
      ? parseInt(
          localStorage.getItem(`liveSessions[${currentClassData.uid}]`),
          10
        ) || 0
      : 0;
    clock.setSeconds(player.totalWatchTime);
    player.setupFlags();
  }, [currentClassData.uid, currentClassData.source]);

  const setSecondsWatched = useCallback(
    (seconds) => {
      player.totalWatchTime = seconds;
      if (localStorageAvailable()) {
        localStorage.setItem(
          `liveSessions[${currentClassData.uid}]`,
          player.totalWatchTime
        );
        if (currentClassData?.programme?.isSpecial) {
          localStorage.setItem(
            'freeTimeRemaining',
            parseInt(localStorage.getItem('freeTimeRemaining'), 10) - 1
          );
        }
      }

      player.sendUaWatch(watchEventInterval);
      player.sendSV();
      player.sendSVFifteenMins();
      player.sendCurrentWatch(handleOnSendWatched);
      player.sendLessonWatched(handleOnSendWatchedPrevious);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentClassData.uid, watchEventInterval]
  );

  const onStart = useCallback(() => {
    clock.totalSeconds += 1;
    setSecondsWatched(clock.totalSeconds);
  }, [setSecondsWatched]);

  const setupForUa = useCallback(() => {
    player.watchEvent = {};
    player.prevUACallTime = 0;
    clock.end();
    player.watchEvent.BROWSER_ID = Cookies.readCookie('BROWSER_ID')
      ? Cookies.readCookie('BROWSER_ID')
      : uaAnalytics.setBrowserId();
    player.watchEvent.PLAYER_ID = uaAnalytics.setPlayerId();
  }, []);

  const setViewer = useCallback(() => {
    viewer.addListener('finished', () => {
      clock.end();
    });
    viewer.addListener(
      'WATCHED',
      (evtName, isPlaying, data, isReplay, isLiveDvr, isLive) => {
        player.playedDuration = data;
        player.dvr = isLiveDvr;
        player.replay = isReplay;
        player.isLive = isLive;
      }
    );
    viewer.addListener(
      'UPDATE_CLASS_START_TIME',
      (evtName, isPlaying, data, isReplay, isLiveDvr, isLive) => {
        player.classStartTime = data;
        player.dvr = isLiveDvr;
        player.replay = isReplay;
        player.isLive = isLive;
      }
    );
    viewer.addListener('playing', (evtName, playing) => {
      if (playing) {
        if (!clock.started) {
          clock.start(onStart);
        } else {
          clock.resume(onStart);
        }
      } else {
        clock.pause();
      }
    });
  }, [onStart]);

  const cleanupPlayerEvents = useCallback(() => {
    if (viewer) {
      viewer.cleanup();
    }
    if (clock) {
      clock.end();
    }
    if (player) {
      player.unmount();
    }
  }, []);

  const setupPlayer = useCallback(
    (processedClassDetails) => {
      player.setup(processedClassDetails, me);
    },
    [me]
  );

  const setupClock = useCallback(() => {
    clock.setup(player.totalWatchTime);
  }, []);

  const setupViewer = useCallback(() => {
    viewer.setup(isExternal);
  }, [isExternal]);

  const {
    sendSubscriptionCardVisibleEventOnIframe,
    sendSubscriptionClickedOnIframe
  } = player;

  return {
    player,
    viewer,
    clock,
    setupPlayer,
    setupClock,
    setupViewer,
    setWatchTime,
    setViewer,
    setupForUa,
    cleanupPlayerEvents,
    sendSubscriptionCardVisibleEventOnIframe,
    sendSubscriptionClickedOnIframe
  };
};

export default usePlayerEvents;
